import { uniqBy } from "lodash";
import { Header, Message, Popup, Segment, Table } from "semantic-ui-react";
import { useNotifySubscriberContactMethodsForLoggedInUser } from "../../../../../../hooks/useNotifySubscriberContactMethodsForLoggedInUser";
import { useNotifySubscriptionGroupsPublic } from "../../../../../../hooks/useNotifySubscriptionGroupsPublic";
import { buildIdentifierString } from "../../../../subscribers/utils/buildIdentifierString";
import { useSubscriberContactMethodPlaceholderFlags } from "../hooks/useSubscriberContactMethodPlaceholderFlags";
import { PreferenceCheckbox } from "./components/PreferenceCheckbox";

export function LoggedInSubscriptionGroupsTable() {
  const { publicSubscriptionGroups } = useNotifySubscriptionGroupsPublic();
  const hasSubscriptionGroups = publicSubscriptionGroups && publicSubscriptionGroups?.length > 0;

  const { subscriberContactMethods } = useNotifySubscriberContactMethodsForLoggedInUser();

  const { userEmail, userPhone, shouldShowEmailPlaceholder, shouldShowPhonePlaceholder } =
    useSubscriberContactMethodPlaceholderFlags();

  const subscriberContactMethodsToShow = [
    ...(subscriberContactMethods?.map((x) => ({ ...x, isPlaceholder: !x.optedInAt })) ?? []),
    ...(shouldShowEmailPlaceholder
      ? [{ id: userEmail, type: "Email" as any, identifier: userEmail, isPlaceholder: true }]
      : []),
    ...(shouldShowPhonePlaceholder
      ? [{ id: userPhone, type: "Phone" as any, identifier: userPhone, isPlaceholder: true }]
      : []),
  ];

  const subscriptionGroups = uniqBy(
    [
      ...(publicSubscriptionGroups ?? []),
      ...(subscriberContactMethods?.flatMap((method) => method.subscriptionGroups) ?? []),
    ],
    "id",
  );

  return (
    <>
      {!hasSubscriptionGroups && (
        <Message>
          <Message.Header>No Subscription Groups</Message.Header>
          <p>There are no subscription groups available for you to subscribe to.</p>
        </Message>
      )}
      {hasSubscriptionGroups && (
        <div>
          {/* Display table when screen is medium or larger */}
          <div className='tw-hidden tw-overflow-x-auto md:tw-block'>
            <Table unstackable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Group</Table.HeaderCell>
                  {subscriberContactMethodsToShow?.map((method) => (
                    <Table.HeaderCell key={method.id} collapsing>
                      <Popup
                        position='top center'
                        hideOnScroll
                        trigger={
                          <div className='tw-w-28 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap'>
                            {buildIdentifierString(method)}
                          </div>
                        }
                      >
                        {buildIdentifierString(method) ?? "No identifier"}
                      </Popup>
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {subscriptionGroups?.map((group) => (
                  <Table.Row key={group.id}>
                    <Table.Cell>
                      <Header as='h4' className='tw-m-0 tw-text-base'>
                        {group.name}
                      </Header>
                      <p className='tw-m-0 tw-text-black-light'>{group.description}</p>
                    </Table.Cell>
                    {subscriberContactMethodsToShow?.map((method) => (
                      <Table.Cell key={method.id}>
                        <PreferenceCheckbox
                          key={method.id}
                          groupId={group.id}
                          subscriberContactMethodId={method.id}
                          type={method.type as "Email" | "Phone"}
                          disabled={method.isPlaceholder}
                          forceOff={method.isPlaceholder}
                        />
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
          {/* Display cards when screen is smaller than medium */}
          <div className='tw-space-y-2 md:tw-hidden'>
            {subscriptionGroups?.map((group) => (
              <Segment key={group.id} className='tw-m-0 tw-flex tw-flex-col tw-gap-3 tw-overflow-hidden'>
                <div className='tw-space-y-1'>
                  <Header as='h3' className='tw-m-0 tw-text-lg'>
                    {group.name}
                  </Header>
                  <p className='tw-m-0'>{group.description}</p>
                </div>
                <div className='tw-flex tw-flex-col tw-gap-2'>
                  {subscriberContactMethodsToShow
                    ?.filter((method) => method.type !== "App")
                    .map((method) => (
                      <PreferenceCheckbox
                        key={method.id}
                        groupId={group.id}
                        subscriberContactMethodId={method.id}
                        type={method.type as "Email" | "Phone"}
                        label={buildIdentifierString(method) ?? undefined}
                        disabled={method.isPlaceholder}
                        forceOff={method.isPlaceholder}
                      />
                    ))}
                </div>
              </Segment>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
